/**
 * Per-signer access codes for a Regulated envelope.
 *
 * Rendered directly beneath the "Consumer / regulated" choice that creates the
 * requirement. That adjacency is the point: the requirement used to be
 * announced here and satisfied in the recipients table several sections above,
 * so the warning named an obligation whose controls were off-screen — and a
 * sender who scrolled up found a narrow column wedged between Role and the
 * delete button. Cause and remedy now sit together, and this is the ONLY place
 * a code is entered.
 *
 * # One-time visibility
 *
 * A saved code is unrecoverable — the server stores a scrypt hash and never
 * returns it. So the plaintext exists only in this form, only until the page
 * is left. Copy has to be offered at the moment the code is generated, and the
 * UI has to say plainly that there is no second chance. This is the recovery-
 * code pattern, and it is why the panel cannot simply live on Review and Send:
 * by then there is nothing left to show.
 */

import { useState } from 'react';
import { Check, Copy, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { generateAccessCode, formatAccessCodeHandoff } from '../accessCode';

export interface AccessCodeSigner {
  /** Stable row key from the recipients table — survives a save. */
  key: string;
  name: string;
  email: string;
  /** True once the server holds a code for this signer. */
  hasStoredCode: boolean;
}

interface AccessCodeAssignmentProps {
  signers: AccessCodeSigner[];
  /** Plaintext codes entered this session, keyed by signer key. */
  codes: Record<string, string>;
  onChange: (codes: Record<string, string>) => void;
  disabled?: boolean;
}

export function AccessCodeAssignment({
  signers,
  codes,
  onChange,
  disabled = false,
}: AccessCodeAssignmentProps) {
  // Which key was copied most recently, so the button can acknowledge it.
  // Clipboard writes are silent, and a button that does nothing visible reads
  // as broken — senders press it again, which is harmless but erodes trust in
  // the one step they cannot verify afterwards.
  const [copied, setCopied] = useState<string | null>(null);

  const set = (key: string, value: string) => onChange({ ...codes, [key]: value });

  /**
   * Fill every signer who has no code in this form. Blanks only, deliberately:
   * a code someone typed by hand is one they have probably already said out
   * loud, and silently replacing it would send the signer a code that no
   * longer matches what they were told. Replacing a value is a per-row act.
   */
  const generateAllBlank = () => {
    const next = { ...codes };
    for (const s of signers) {
      if ((next[s.key] ?? '').trim() === '') next[s.key] = generateAccessCode();
    }
    onChange(next);
  };

  const copy = async (key: string, text: string) => {
    if (text.trim() === '') return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
    } catch {
      // A denied clipboard permission is not an error worth interrupting for —
      // the code is on screen and can be read off it.
    }
  };

  const handoff = formatAccessCodeHandoff(
    signers.map((s) => ({ name: s.name, email: s.email, code: codes[s.key] ?? '' })),
  );
  const enteredCount = signers.filter((s) => (codes[s.key] ?? '').trim() !== '').length;

  if (signers.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Add a signer in step 2 and their access code will appear here.
      </p>
    );
  }

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <ul className="space-y-2">
        {signers.map((s) => {
          const value = codes[s.key] ?? '';
          const inputId = `access-code-${s.key}`;
          return (
            <li key={s.key} className="flex flex-wrap items-end gap-2">
              <div className="min-w-0 flex-1 space-y-1">
                <Label htmlFor={inputId} className="text-xs">
                  {s.name.trim() || s.email.trim() || 'Unnamed signer'}
                </Label>
                <Input
                  id={inputId}
                  value={value}
                  onChange={(e) => set(s.key, e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={disabled}
                  className="font-mono"
                  placeholder={s.hasStoredCode ? 'Code set — type to replace' : ''}
                  aria-describedby={s.hasStoredCode ? `${inputId}-stored` : undefined}
                />
                {s.hasStoredCode && (
                  <p id={`${inputId}-stored`} className="text-[11px] text-muted-foreground">
                    A code is already set. Leave this blank to keep it.
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => set(s.key, generateAccessCode())}
                aria-label={`Generate an access code for ${s.name.trim() || s.email.trim() || 'this signer'}`}
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled || value.trim() === ''}
                onClick={() => void copy(s.key, value.trim())}
                aria-label={`Copy the access code for ${s.name.trim() || s.email.trim() || 'this signer'}`}
              >
                {copied === s.key ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={generateAllBlank}
        >
          Generate all
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || handoff === ''}
          onClick={() => void copy('__all__', handoff)}
        >
          {copied === '__all__' ? (
            <Check className="h-3.5 w-3.5 mr-2" />
          ) : (
            <Copy className="h-3.5 w-3.5 mr-2" />
          )}
          Copy all
        </Button>
        <span className="text-[11px] text-muted-foreground">
          {enteredCount} of {signers.length} entered · Generate all fills blanks only
        </span>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Copy these now — codes are stored hashed and can never be shown again. Share them by phone
        or in person, never in the signing email.
      </p>
    </div>
  );
}
